import { PropTypes } from 'prop-types';
import { Link } from 'react-router-dom';
import { Score, TagBadges } from '../';
import { useSelector, useDispatch } from 'react-redux';
import {
    PATH_ACCOUNT_REVIEWS,
    PATH_RESTAURANT_REVIEWS,
    PATH_VARIABLE_ACCOUNT_ID,
    PATH_VARIABLE_RESTAURANT_ID
} from '../../../constants.js'
import '../style.css'
import { FaEdit, FaTrash, FaThumbsUp, FaThumbsDown, FaHeart } from "react-icons/fa";
import EditReviewModal from '../../EditReview/EditReviewModal.jsx';
import DeleteReviewModal from '../../DeleteReview/DeleteReviewModal.jsx';
import { reviewsActions } from '../../../redux/reducers/reviews';
import { useState, useCallback, useMemo } from 'react';

const propTypes = {
    review: PropTypes.object.isRequired,
    restaurant: PropTypes.object
};

const REACTION_KEY_BY_TYPE = {
    THUMBS_UP: "thumbsUp",
    THUMBS_DOWN: "thumbsDown",
    HEART: "heart",
};

const ReviewCard = ({ review, restaurant }) => {
    const dispatch = useDispatch();
    const userAccountId = useSelector((state)=>state.userReducer.userData?.sub);
    const loggedIn = useSelector((state) => state.userReducer.loggedIn);
    const updatedReview = useSelector((state) => state.reviewsReducer.reviews?.find(r => r.reviewId === review.reviewId && r.accountId === review.accountId));
    const isReviewAuthor = useMemo(() => userAccountId === review.accountId, [userAccountId, review.accountId]);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const idToken = useSelector((state) => state.userReducer.idToken);
    const displayReview = updatedReview ?? review;
    const reactionCounts = displayReview?.reactionCounts || {};
    const myReactions = displayReview?.myReactions || {};

    const editSave = useCallback(()=>{
        setIsEditModalOpen((prev)=>!prev);
    },[])

    const deleteReview = useCallback(()=>{
        setIsDeleteModalOpen((prev)=>!prev);
    },[])

    const onToggleReaction = useCallback((reactionType) => {
        if (!loggedIn || !idToken) {
            return;
        }
        const reactionKey = REACTION_KEY_BY_TYPE[reactionType];
        if (!reactionKey) {
            return;
        }
        const currentlyOn = !!myReactions?.[reactionKey];
        dispatch(reviewsActions.startToggleReactionRequest(
            displayReview.reviewId,
            displayReview.accountId,
            reactionType,
            !currentlyOn,
            idToken
        ));
    }, [dispatch, loggedIn, idToken, myReactions, displayReview]);

    return (
        <>
            <div className="relative mb-3 w-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div>
                        <h3 className="inline-block text-xl font-semibold">
                        {updatedReview?.title || review.title}
                        </h3>
                        <Score size="md" score={review.score} />
                        {isReviewAuthor && ( <FaEdit style={{ fontSize: '24px', position: 'absolute', top: '19px', right: '50px', cursor: 'pointer' }} onClick={editSave} /> )}
                        {isReviewAuthor && ( <FaTrash style={{ fontSize: '24px', position: 'absolute', top: '19px', right: '15px', cursor: 'pointer' }} onClick={deleteReview} /> )}

                    </div>
                    { restaurant &&
                        <div>
                            <h5 className="mb-2 mr-2 inline text-lg text-slate-900">
                                <Link to={`${PATH_RESTAURANT_REVIEWS}`.replace(PATH_VARIABLE_RESTAURANT_ID, restaurant.id)}>{restaurant.displayName.text}</Link>
                            </h5>
                            <p className="mb-2 inline text-slate-600">
                                {restaurant.formattedAddress}
                            </p>
                        </div>
                    }
                    <h6 className="mb-3 text-sm text-slate-500">
                        By: { review.accountId
                            ? <Link to={`${PATH_ACCOUNT_REVIEWS}`.replace(PATH_VARIABLE_ACCOUNT_ID, review.accountId)}>
                                {review.userMetadata ? review.userMetadata.username : (review.authorId ? review.authorId : review.accountId)}
                            </Link>
                            : <div className="inline">{review.authorId}</div> }
                    </h6>
                    <p className="text-slate-800">
                        {review.body}
                    </p>
                    <div className="mb-2 flex items-center gap-3">
                        <button
                            type="button"
                            className="flex items-center gap-1 border-0 bg-transparent p-0 disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={() => onToggleReaction("THUMBS_UP")}
                            disabled={!loggedIn}
                            title={loggedIn ? "Toggle thumbs up" : "Sign in to react"}
                        >
                            <FaThumbsUp color={myReactions.thumbsUp ? "#0d6efd" : "#6c757d"} />
                            <span>{reactionCounts.thumbsUp ?? 0}</span>
                        </button>
                        <button
                            type="button"
                            className="flex items-center gap-1 border-0 bg-transparent p-0 disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={() => onToggleReaction("THUMBS_DOWN")}
                            disabled={!loggedIn}
                            title={loggedIn ? "Toggle thumbs down" : "Sign in to react"}
                        >
                            <FaThumbsDown color={myReactions.thumbsDown ? "#dc3545" : "#6c757d"} />
                            <span>{reactionCounts.thumbsDown ?? 0}</span>
                        </button>
                        <button
                            type="button"
                            className="flex items-center gap-1 border-0 bg-transparent p-0 disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={() => onToggleReaction("HEART")}
                            disabled={!loggedIn}
                            title={loggedIn ? "Toggle heart" : "Sign in to react"}
                        >
                            <FaHeart color={myReactions.heart ? "#dc3545" : "#6c757d"} />
                            <span>{reactionCounts.heart ?? 0}</span>
                        </button>
                    </div>
                    <TagBadges tags={updatedReview?.tags || review.tags} className="mt-2" />
                    {review.isoDateTime &&
                        <h6 className="mb-2 italic text-slate-500">
                            {new Date(review.isoDateTime).toLocaleString()}
                        </h6>
                    }
            </div>
            <EditReviewModal
                review={review}
                save={editSave}
                modal={isEditModalOpen}
                signIn={userAccountId}
            />
            <DeleteReviewModal
                reviewId={review.reviewId}
                option={deleteReview}
                modal={isDeleteModalOpen}
                signIn={userAccountId}
            />
        </>
    )
}

ReviewCard.propTypes = propTypes;

export default ReviewCard;
